import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getGscSnapshot,
  getGscRange,
  refreshGscNow,
  setGscProperty,
  GSC_RANGES,
  type GscRangeId,
  type GscMetricRow,
} from "@/lib/gsc.functions";


const fmtInt = new Intl.NumberFormat("sl-SI", { maximumFractionDigits: 0 }).format;
const fmtPct = (n: number) => `${new Intl.NumberFormat("sl-SI", { maximumFractionDigits: 1 }).format(n * 100)} %`;
const fmtPos = (n: number) => new Intl.NumberFormat("sl-SI", { maximumFractionDigits: 1 }).format(n);
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDay = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

function delta(now: number, prev: number) {
  if (!prev) return null;
  return Math.round(((now - prev) / prev) * 100);
}

export function GscSection() {
  const fetchSnapshot = useServerFn(getGscSnapshot);
  const fetchRange = useServerFn(getGscRange);
  const refresh = useServerFn(refreshGscNow);
  const setProperty = useServerFn(setGscProperty);
  const qc = useQueryClient();

  const [range, setRange] = useState<GscRangeId>("28");
  const rangeLabel = GSC_RANGES.find((r) => r.id === range)?.label ?? "28 dni";

  const q = useQuery({
    queryKey: ["gsc-snapshot", range],
    queryFn: () => (range === "28" ? fetchSnapshot() : fetchRange({ data: { range } })),
    staleTime: 5 * 60_000,
  });

  const refreshMutation = useMutation({
    mutationFn: () => refresh(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gsc-snapshot"] }),
  });

  const propertyMutation = useMutation({
    mutationFn: (siteUrl: string) => setProperty({ data: { siteUrl } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gsc-snapshot"] }),
  });

  const d = q.data;
  const candidates =
    (refreshMutation.data as { candidates?: string[] } | undefined)?.candidates ?? d?.candidates;

  return (
    <div className="border border-border p-5 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Google Search Console</div>
          <h3 className="font-serif text-xl mt-1">Uspešnost v Google iskanju</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Obdobje: {rangeLabel}
            {d?.rangeFrom ? ` (${fmtDay(d.rangeFrom)} – ${fmtDay(d.rangeTo)})` : ""} · samodejna posodobitev vsak dan ob 7.00
            {d?.refreshedAt ? ` · nazadnje ${fmtDate(d.refreshedAt)}` : ""}
            {d?.siteUrl ? ` · ${d.siteUrl}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          className="border border-border px-4 py-2 text-sm hover:border-accent disabled:opacity-60 min-h-11"
        >
          {refreshMutation.isPending ? "Osvežujem…" : "Osveži zdaj"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Izbira obdobja">
        {GSC_RANGES.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setRange(r.id)}
            aria-pressed={range === r.id}
            className={`border px-3 py-2 text-sm min-h-11 transition-colors ${
              range === r.id
                ? "border-accent bg-accent/10 text-foreground font-medium"
                : "border-border text-muted-foreground hover:border-accent"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>


      {q.isLoading ? (
        <div className="text-sm text-muted-foreground">Nalagam…</div>
      ) : q.error ? (
        <div className="text-sm text-destructive">Napaka: {(q.error as Error).message}</div>
      ) : (
        <>
          {candidates?.length ? (
            <div className="border border-amber-300 bg-amber-50 p-4 text-sm space-y-2">
              <div>Izberite lastnino v Search Console, ki jo želite spremljati:</div>
              <div className="flex flex-wrap gap-2">
                {candidates.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => propertyMutation.mutate(c)}
                    className="border border-border bg-background px-3 py-2 text-xs hover:border-accent"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {d?.error ? (
            <div className="border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm break-words">{d.error}</div>
          ) : null}

          {!d?.refreshedAt && !candidates?.length ? (
            <div className="text-sm text-muted-foreground">
              Podatkov še ni. Kliknite »Osveži zdaj« za prvi prenos.
            </div>
          ) : null}

          {d?.refreshedAt ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Metric label="Kliki" value={fmtInt(d.totals.clicks)} delta={delta(d.totals.clicks, d.prevTotals.clicks)} compare={rangeLabel} />
                <Metric label="Prikazi" value={fmtInt(d.totals.impressions)} delta={delta(d.totals.impressions, d.prevTotals.impressions)} compare={rangeLabel} />
                <Metric label="Povpr. CTR" value={fmtPct(d.totals.ctr)} delta={delta(d.totals.ctr, d.prevTotals.ctr)} compare={rangeLabel} />
                <Metric label="Povpr. pozicija" value={fmtPos(d.totals.position)} delta={delta(d.prevTotals.position, d.totals.position)} compare={rangeLabel} />
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <RowsTable title="Najuspešnejše poizvedbe" rows={d.topQueries} />
                <RowsTable title="Najuspešnejše strani" rows={d.topPages} isPage />
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

function Metric({ label, value, delta: dlt, compare }: { label: string; value: string; delta: number | null; compare: string }) {
  return (
    <div className="border border-border p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-serif text-2xl">{value}</div>
      {dlt !== null ? (
        <div className={`text-xs mt-1 ${dlt >= 0 ? "text-green-700" : "text-red-700"}`}>
          {dlt >= 0 ? "▲" : "▼"} {Math.abs(dlt)} % vs. prejšnje obdobje ({compare})
        </div>
      ) : (
        <div className="text-xs mt-1 text-muted-foreground">—</div>
      )}
    </div>
  );
}

function RowsTable({ title, rows, isPage }: { title: string; rows: GscMetricRow[]; isPage?: boolean }) {
  if (!rows.length) return null;
  return (
    <div className="border border-border">
      <div className="border-b border-border px-4 py-3 text-sm font-medium">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
              <th className="py-2 px-4 font-medium">{isPage ? "Stran" : "Poizvedba"}</th>
              <th className="py-2 px-2 font-medium text-right">Kliki</th>
              <th className="py-2 px-2 font-medium text-right">Prikazi</th>
              <th className="py-2 px-2 font-medium text-right">CTR</th>
              <th className="py-2 px-4 font-medium text-right">Poz.</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 15).map((r) => (
              <tr key={r.key} className="border-b border-border/60 last:border-0">
                <td className="py-2 px-4 max-w-[22rem] truncate">
                  {isPage ? (
                    <a href={r.key} target="_blank" rel="noopener" className="hover:text-accent underline-offset-2 hover:underline">
                      {r.key.replace(/^https?:\/\/[^/]+/, "") || "/"}
                    </a>
                  ) : (
                    r.key
                  )}
                </td>
                <td className="py-2 px-2 text-right tabular-nums">{fmtInt(r.clicks)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{fmtInt(r.impressions)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{fmtPct(r.ctr)}</td>
                <td className="py-2 px-4 text-right tabular-nums">{fmtPos(r.position)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
